import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { passiPer } from "@/components/demo/passiDemo";
import { DemoFinale } from "@/components/demo/DemoFinale";
import { ChevronLeft, X, HelpCircle, Check } from "lucide-react";

const CHIAVE = "rh_demo_tour";

export const tourAttivo = () => localStorage.getItem(CHIAVE) === "1";
export const avviaTour = () => localStorage.setItem(CHIAVE, "1");
export const spegniTour = () => localStorage.removeItem(CHIAVE);

const posizione = (sel) => {
  for (const s of sel) {
    const el = document.querySelector(s);
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.width && r.height && r.bottom > 0 && r.top < window.innerHeight) return r;
    }
  }
  return null;
};

/**
 * Prova guidata della demo: un alone sul punto da toccare, una frase per passo e una barra
 * in basso con l'avanzamento. Non blocca nulla: si tocca l'app vera, non una finta.
 */
export const PercorsoDemo = () => {
  const { demoMode, user } = useApp();
  const PASSI = passiPer(user?.role);
  const [on, setOn] = useState(tourAttivo());
  const [i, setI] = useState(0);
  const [box, setBox] = useState(null);
  const [aiuto, setAiuto] = useState(false);
  const [mini, setMini] = useState(false);
  const [finale, setFinale] = useState(false);
  const visto = useRef({ righe: 0, inviato: false, conto: false, fondo: 0 });
  const portato = useRef({});
  const nav = useNavigate();

  const passo = PASSI[i];

  const chiudi = useCallback(() => {
    spegniTour();
    setOn(false);
  }, []);

  // Il punto da toccare si muove con la pagina: si rilegge la posizione a ritmo costante
  useEffect(() => {
    if (!on || !demoMode || finale || !passo) return;
    const tick = () => {
      const fuori = passo.dove && !window.location.pathname.startsWith(passo.dove);
      // La guida porta lei nella pagina giusta: «tocca un tavolo» senza i tavoli sotto gli occhi
      // non vuol dire niente. Si fa una volta per tappa, poi il visitatore è libero.
      if (fuori && !portato.current[passo.id]) {
        portato.current[passo.id] = true;
        nav(passo.dove);
        return;
      }
      setBox(posizione(fuori && passo.selFuori ? passo.selFuori : passo.sel));

      // Si guarda solo quello che accade durante il percorso: i piatti messi adesso nella comanda,
      // l'invio ai reparti appena fatto, il conto appena aperto.
      const s = visto.current;
      const righe = document.querySelectorAll("[data-testid^='cart-line-']").length;
      if (document.querySelector("[data-testid='send-summary-dialog']") || (s.righe > 0 && righe === 0)) {
        s.inviato = true;
      }
      if (document.querySelector("[data-testid='conto-dialog']")) s.conto = true;
      // Il fondo cassa non si «fa»: si guarda. La tappa si chiude dopo qualche secondo sul pannello.
      if (window.location.pathname.startsWith("/cassa")
          && document.querySelector("[data-testid='cash-floats-panel']")) s.fondo += 1;
      s.righe = Math.max(s.righe, righe);
      if (righe === 0 && !s.inviato) s.righe = 0;

      if (passo.fatto?.(s)) {
        if (i === PASSI.length - 1) {
          setFinale(true);
          spegniTour();
        } else {
          setI(i + 1);
          setAiuto(false);
        }
        return;
      }
      // Se il visitatore corre avanti da solo (manda in cucina o apre il conto senza seguire la
      // guida), si salta alle tappe che ha già fatto davvero.
      let oltre = -1;
      PASSI.forEach((x, n) => {
        if (n > i && x.fatto?.(s)) oltre = n;
      });
      if (oltre === PASSI.length - 1) {
        setFinale(true);
        spegniTour();
      } else if (oltre > i) {
        setI(oltre + 1);
        setAiuto(false);
      }
    };
    tick();
    const id = setInterval(tick, 500);
    // Se il visitatore scorre la pagina vuole guardare: la guida si fa da parte da sola
    const scorre = () => setMini(true);
    window.addEventListener("scroll", tick, true);
    window.addEventListener("touchmove", scorre, { passive: true });
    window.addEventListener("wheel", scorre, { passive: true });
    window.addEventListener("resize", tick);
    return () => {
      clearInterval(id);
      window.removeEventListener("scroll", tick, true);
      window.removeEventListener("touchmove", scorre);
      window.removeEventListener("wheel", scorre);
      window.removeEventListener("resize", tick);
    };
  }, [on, demoMode, finale, passo, i, PASSI, nav]);

  if (!demoMode) return null;
  if (finale) return <DemoFinale onClose={() => setFinale(false)} />;
  if (!on || !passo) return null;

  // La guida non oscura niente: si mette dalla parte opposta al punto da toccare e si può ridurre
  // a una riga, così nessun pulsante resta mai coperto.
  const inBasso = !!box && box.bottom > window.innerHeight - 260;
  const stile = inBasso ? { top: 92 } : { bottom: 84 };

  // Se è aperta una finestra dell'app (conto, opzioni del piatto) la guida si fa da parte
  if (mini || document.querySelector("[role='dialog']"))
    return (
      <button
        onClick={() => setMini(false)}
        style={stile}
        className="pointer-events-auto fixed left-3 right-3 z-[72] mx-auto flex max-w-[420px] items-center gap-2 rounded-full border border-amber-400/40 bg-slate-900/95 px-4 py-2.5 text-xs font-semibold text-slate-100 shadow-2xl"
        data-testid="demo-progress-resume-btn"
      >
        <span className="font-num text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400">
          {i + 1}/{PASSI.length}
        </span>
        <span className="truncate">{passo.titolo}</span>
        <span className="ml-auto shrink-0 text-amber-400">Apri la guida</span>
      </button>
    );

  return (
    <>
      {box && (passo.unico || (passo.dove && !window.location.pathname.startsWith(passo.dove))) && (
        <div
          className="pointer-events-none fixed z-[70] rounded-xl ring-[3px] ring-amber-400 transition-all duration-300"
          style={{ top: box.top - 4, left: box.left - 4, width: box.width + 8, height: box.height + 8,
                   boxShadow: "0 0 24px rgba(251,191,36,.55)" }}
          data-testid="demo-coachmark-spotlight"
        />
      )}

      <div
        className="pointer-events-auto fixed left-3 right-3 z-[72] mx-auto max-w-[420px] rounded-2xl border border-amber-400/40 bg-slate-900/95 px-3.5 py-3 text-slate-100 shadow-2xl backdrop-blur-sm"
        style={stile}
        data-testid="demo-coachmark-popover"
      >
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-num text-[10px] font-bold uppercase tracking-[0.24em] text-amber-400"
               data-testid="demo-progress-step-text">
              Passo {i + 1} di {PASSI.length}
            </p>
            <p className="font-head mt-0.5 text-sm font-bold">{passo.titolo}</p>
          </div>
          <button onClick={() => setMini(true)} data-testid="demo-progress-mini-btn"
                  className="shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold text-slate-400">
            Riduci
          </button>
          <button onClick={chiudi} data-testid="demo-progress-exit-btn"
                  className="shrink-0 rounded-md p-1 text-slate-400" aria-label="Esci dalla guida">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1 text-[13px] leading-snug text-slate-300">{passo.testo}</p>
        {aiuto && (
          <p className="mt-2 rounded-lg bg-slate-800 p-2.5 text-xs text-slate-300" data-testid="demo-coachmark-aiuto">
            {passo.aiuto}
          </p>
        )}
        {passo.dove && !window.location.pathname.startsWith(passo.dove) && (
          <p className="mt-1.5 text-xs font-semibold text-amber-300" data-testid="demo-coachmark-dove">
            {passo.doveTesto || "Tocca «Sala», in fondo allo schermo: ti porto dai tavoli."}
          </p>
        )}

        <div className="mt-2 flex items-center gap-2">
          <button onClick={() => setAiuto((v) => !v)} data-testid="demo-coachmark-help-btn"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-300">
            <HelpCircle className="h-3.5 w-3.5" /> Come si fa
          </button>
          <span className="flex-1" />
          {i > 0 && (
            <button onClick={() => setI(i - 1)} data-testid="demo-progress-back-btn"
                    className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-slate-400">
              <ChevronLeft className="h-3.5 w-3.5" /> Indietro
            </button>
          )}
          <button
            onClick={() => (i === PASSI.length - 1 ? (setFinale(true), spegniTour()) : setI(i + 1))}
            data-testid="demo-coachmark-next-btn"
            className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-2.5 py-1.5 text-[11px] font-bold text-slate-950"
          >
            {i === PASSI.length - 1 ? <><Check className="h-3.5 w-3.5" /> Ho finito</> : "Salta"}
          </button>
        </div>

        <div className="mt-2 flex gap-1" data-testid="demo-progress-banner">
          {PASSI.map((p, n) => (
            <span key={p.id} className={`h-1 flex-1 rounded-full ${n <= i ? "bg-amber-400" : "bg-slate-700"}`} />
          ))}
        </div>
      </div>
    </>
  );
};
